import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Layers, Scissors, Shirt, Type, Zap, Coffee,
  CheckCircle, ArrowRight, Star, Quote
} from "lucide-react";

const services = [
  {
    id: "impression-dtf",
    title: "Impression DTF",
    subtitle: "Transfert numérique",
    description: "Impression haute qualité sur textiles. Idéal pour t-shirts, sacs et vêtements.",
    icon: Layers,
    color: "accent-green"
  },
  {
    id: "sublimation",
    title: "Sublimation",
    subtitle: "Tasses, gourdes, objets",
    description: "Personnalisation durable sur céramique, métal et polyester.",
    icon: Coffee,
    color: "accent-red"
  },
  {
    id: "serigraphie",
    title: "Sérigraphie",
    subtitle: "Grandes séries",
    description: "Technique professionnelle pour séries importantes et couleurs vives.",
    icon: Shirt,
    color: "accent-yellow"
  },
  {
    id: "flocage-transfert",
    title: "Flocage & Flex",
    subtitle: "Marquage textile",
    description: "Noms, numéros et logos sur textiles professionnels et sportifs.",
    icon: Type,
    color: "accent-green"
  },
  {
    id: "gravure-plaque",
    title: "Gravure & Plaques",
    subtitle: "Plexiglass & supports",
    description: "Plaques personnalisées, signalétique et gravure de précision.",
    icon: Zap,
    color: "accent-red"
  },
  {
    id: "infographie",
    title: "Infographie",
    subtitle: "Conception graphique",
    description: "Création de visuels, affiches, cartes de visite et supports marketing.",
    icon: Scissors,
    color: "accent-yellow"
  }
];

const benefits = [
  "Conseil sur le fichier et le rendu final",
  "Tests et épreuves si nécessaire",
  "Qualité d'impression professionnelle",
  "Finitions soignées et contrôle qualité",
  "Livraison ou retrait en atelier"
];

const equipment = [
  { name: "Presse textile", desc: "Transfert DTF et sublimation" },
  { name: "Presse à mugs", desc: "Sublimation haute qualité" },
  { name: "Machine sérigraphie", desc: "Impression grandes séries" },
  { name: "Plotter de découpe", desc: "Flex et vinyle précis" },
  { name: "Imprimante grand format", desc: "Affiches et kakémonos" },
  { name: "Gravure", desc: "Plaques et signalétique" }
];

const portfolio = [
  { 
    img: "/images/realisations/real-1.jpeg", 
    title: "T-shirt personnalisé",
    category: "Textile"
  },
  { 
    img: "/images/realisations/real-3.jpeg", 
    title: "Plaque commémorative",
    category: "Gravure"
  },
  { 
    img: "/images/realisations/real-4.jpeg", 
    title: "Impression photo",
    category: "Sublimation"
  },
  { 
    img: "/images/realisations/real-10.jpeg", 
    title: "Textile événementiel",
    category: "Textile"
  },
  { 
    img: "/images/realisations/real-15.jpeg", 
    title: "Création graphique",
    category: "Impression"
  },
  { 
    img: "/images/realisations/real-18.jpeg", 
    title: "Goodies personnalisés",
    category: "Goodies"
  }
];

const processSteps = [
  { num: "01", title: "Brief & Usage", desc: "Compréhension de votre projet et de vos besoins" },
  { num: "02", title: "Vérification fichiers", desc: "Contrôle technique et conseils d'optimisation" },
  { num: "03", title: "Production", desc: "Impression avec notre parc machines professionnel" },
  { num: "04", title: "Contrôle & Livraison", desc: "Vérification qualité et remise de votre commande" }
];

const testimonials = [
  {
    text: "Service impeccable et qualité au rendez-vous. Les t-shirts pour notre événement étaient parfaits.",
    author: "Marie L.",
    company: "Startup Genève"
  },
  {
    text: "Réactifs, professionnels et de bon conseil. Je recommande pour tout projet de signalétique.",
    author: "Thomas B.",
    company: "Agence Design"
  },
  {
    text: "La découpe laser est d'une précision remarquable. Très satisfait du résultat final.",
    author: "Julien M.",
    company: "Artisan Créateur"
  }
];

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const stagger = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true },
  transition: { staggerChildren: 0.1 }
};

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section 
        className="min-h-screen flex items-center relative overflow-hidden pt-20"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1580122252289-8eccefa9ce2e?w=1920&h=1080&fit=crop')",
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-background/95" />
        <div className="container-custom relative z-10 py-20">
          <div className="max-w-3xl">
            <motion.p 
              className="eyebrow mb-4 flex items-center gap-2"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="w-8 h-px bg-muted" />
              Fort-de-France, Martinique
            </motion.p>
            
            <motion.h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              data-testid="hero-title"
            >
              Impression & personnalisation professionnelle
            </motion.h1>
            
            <motion.p 
              className="text-lg md:text-xl text-muted mb-8 max-w-xl"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Du prototype à la série. Qualité, précision, délais maîtrisés.
            </motion.p>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 mb-12"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Link to="/devis" className="btn-primary" data-testid="hero-cta-devis">
                Demander un devis
              </Link>
              <Link to="/services" className="btn-outline" data-testid="hero-cta-services">
                Voir nos services
              </Link>
            </motion.div>
            
            {/* Trust badges */}
            <motion.div 
              className="flex flex-wrap gap-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {["Impression DTF & Sublimation", "Sérigraphie pro", "Tous supports", "Délais rapides"].map((badge, i) => (
                <span key={i} className="equipment-badge text-xs">
                  <CheckCircle size={14} className="text-[hsl(var(--accent-green))]" />
                  {badge}
                </span>
              ))}
            </motion.div>
          </div>
        </div>
        
        {/* Decorative accent */}
        <div className="absolute bottom-0 right-0 w-1/3 h-2 bg-foreground hidden md:block" />
      </section>

      {/* Services Section */}
      <section className="section-padding bg-secondary/30" data-testid="services-section">
        <div className="container-custom">
          <motion.div className="mb-16" {...fadeInUp}>
            <p className="eyebrow mb-3">Nos expertises</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Services</h2>
            <p className="text-muted max-w-xl">
              Un parc machines complet pour répondre à tous vos besoins d'impression et de personnalisation.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => (
              <motion.div
                key={service.id}
                className="card-swiss group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`service-card-${service.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-10 h-10 flex items-center justify-center ${service.color}`}>
                    <service.icon size={20} className="text-background" />
                  </div>
                  <ArrowRight size={18} className="text-muted group-hover:text-foreground group-hover:translate-x-1 transition-all" />
                </div>
                <p className="font-mono text-xs text-muted mb-2">{service.subtitle}</p>
                <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                <p className="text-muted text-sm">{service.description}</p>
              </motion.div>
            ))}
          </div>
          
          <motion.div className="mt-12 text-center" {...fadeInUp}>
            <Link to="/services" className="btn-outline" data-testid="services-link">
              Découvrir tous nos services
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding" data-testid="benefits-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <p className="eyebrow mb-3">Pourquoi nous choisir</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Ce que vous obtenez</h2>
              <p className="text-muted mb-8">
                Un accompagnement complet, de la conception à la livraison. 
                Notre expertise garantit un résultat à la hauteur de vos attentes.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle size={20} className="text-[hsl(var(--accent-green))] flex-shrink-0 mt-0.5" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            
            <motion.div 
              className="relative"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="aspect-[4/3] bg-secondary overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1729944950511-e9c71556cfd4?w=800&h=600&fit=crop"
                  alt="Atelier d'impression"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-foreground flex items-center justify-center">
                <span className="text-background text-xs font-mono text-center leading-tight">
                  QUALITÉ<br/>GARANTIE
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Equipment Section */}
      <section className="section-padding bg-foreground text-background" data-testid="equipment-section">
        <div className="container-custom">
          <motion.div className="mb-12" {...fadeInUp}>
            <p className="eyebrow text-background/50 mb-3">Parc machines professionnel</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Notre matériel</h2>
            <p className="text-background/70 max-w-xl">
              Des équipements de pointe pour garantir précision, régularité et qualité sur chaque projet.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {equipment.map((item, i) => (
              <motion.div
                key={item.name}
                className="border border-background/20 p-6 hover:border-background/50 transition-colors"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
              >
                <h3 className="font-bold mb-2">{item.name}</h3>
                <p className="text-background/60 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Preview Section */}
      <section className="section-padding" data-testid="portfolio-section">
        <div className="container-custom">
          <motion.div className="mb-12" {...fadeInUp}>
            <p className="eyebrow mb-3">Portfolio</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Nos réalisations</h2>
            <p className="text-muted max-w-xl">
              Découvrez quelques-uns de nos projets récents.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {portfolio.map((item, i) => (
              <motion.div
                key={i}
                className="img-container group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <div className="aspect-[4/3] overflow-hidden bg-secondary">
                  <img 
                    src={item.img} 
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="mt-4">
                  <p className="font-mono text-xs text-muted mb-1">{item.category}</p>
                  <h3 className="font-bold">{item.title}</h3>
                </div>
              </motion.div>
            ))}
          </div>
          
          <motion.div className="mt-12 text-center" {...fadeInUp}>
            <Link to="/realisations" className="btn-outline" data-testid="portfolio-link">
              Voir toutes nos réalisations
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-secondary/30" data-testid="process-section">
        <div className="container-custom">
          <motion.div className="mb-12" {...fadeInUp}>
            <p className="eyebrow mb-3">Notre méthode</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Process</h2>
            <p className="text-muted max-w-xl">
              Une approche structurée pour garantir le succès de votre projet.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, i) => (
              <motion.div
                key={step.num}
                className="relative"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <span className="font-mono text-5xl font-bold text-border">{step.num}</span>
                <h3 className="text-xl font-bold mt-2 mb-2">{step.title}</h3>
                <p className="text-muted text-sm">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding" data-testid="testimonials-section">
        <div className="container-custom">
          <motion.div className="mb-12" {...fadeInUp}>
            <p className="eyebrow mb-3">Témoignages</p>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ils nous font confiance</h2>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, i) => (
              <motion.div
                key={i}
                className="testimonial-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <Quote size={24} className="text-muted mb-4" />
                <p className="mb-4">{testimonial.text}</p>
                <div>
                  <p className="font-bold">{testimonial.author}</p>
                  <p className="text-muted text-sm">{testimonial.company}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-foreground text-background" data-testid="cta-section">
        <div className="container-custom text-center">
          <motion.div {...fadeInUp}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Prêt à donner vie à votre projet ?
            </h2>
            <p className="text-background/70 mb-8 max-w-lg mx-auto">
              Contactez-nous pour discuter de vos besoins. Réponse sous 24-48h ouvrées.
            </p>
            <Link to="/devis" className="btn-outline border-background text-background hover:bg-background hover:text-foreground" data-testid="cta-devis">
              Demander un devis
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
